import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useNavigateWithBreadcrumbs } from '../../hooks';
import i18n from '../../utilities/i18n';
import StudyHeader from './studyComponents/StudyHeader';
import { Site, Subject } from '../../models';
import { EntityStatus, EntityType } from 'adc-sirius-module-common';
import MessageBanner from '../messageBanner/MessageBanner';
import {
  Button,
  LinkText,
  LoadingSpinner,
  Table,
  VStack,
} from '@adc/parallax-component-library';
import CreateSubjectModal from '../modal/CreateSubjectModal';
import DynamicTable, {
  CheckboxFilterSection,
  ColumnConfig,
} from '../table/DynamicTable';
import CreateSiteModal from '../modal/CreateSiteModal';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import {
  createSiteMutateOptions,
  createSubjectMutateOptions,
  devicesByStudyQueryOptions,
  sitesQueryOptions,
  studyQueryOptions,
  subjectsQueryOptions,
} from '../../queryOptions';
import { AssignDeviceModal } from '../deviceAssignment/AssignDeviceModal';
import { enumToCheckboxOptions } from '../../utilities/helpers';

/**
 * Study component that displays the details of a specific study.
 * @returns {React.JSX.Element}
 */
export default function Study() {
  const queryClient = useQueryClient();
  const navigateWithBreadcrumbs = useNavigateWithBreadcrumbs();

  const navigateToSite = (siteKey: string, siteLabel: string) => {
    navigateWithBreadcrumbs(
      `/sites/${siteKey}`,
      siteLabel,
      study.data?.name || i18n.t('Page_Study.title'),
    );
  };

  const navigateToSubject = (subjectKey: string, subjectLabel: string) => {
    navigateWithBreadcrumbs(
      `/subjects/${subjectKey}`,
      subjectLabel,
      study.data?.name || i18n.t('Page_Study.title'),
    );
  };

  const { studyKey } = useParams();

  const [study, sites, devices] = useQueries({
    queries: [
      studyQueryOptions({ key: studyKey }),
      sitesQueryOptions({ studyKey }),
      devicesByStudyQueryOptions(
        {
          key: studyKey,
        },
        { enabled: !!studyKey },
      ),
    ],
  });

  const subjects = useQueries({
    queries: sites.isSuccess
      ? sites.data.map((site) => subjectsQueryOptions({ siteKey: site.key }))
      : [],
    combine: (results) => {
      return {
        data: results
          .flatMap((result) => result.data)
          .filter((subject) => !!subject),
        pending: results.some((result) => result.isPending),
      };
    },
  });

  const [filteredSites, setFilteredSites] = useState<Site[]>([]);
  const [siteStatusFilters, setSiteStatusFilters] = useState<
    CheckboxFilterSection[]
  >([
    {
      id: 'status',
      label: i18n.t('Entity.status'),
      items: enumToCheckboxOptions(EntityStatus, 'Status'),
    },
  ]);

  const [filteredSubjects, setFilteredSubjects] = useState<Subject[]>([]);
  const [subjectStatusFilters, setSubjectStatusFilters] = useState<
    CheckboxFilterSection[]
  >([
    {
      id: 'status',
      label: i18n.t('Entity.status'),
      items: enumToCheckboxOptions(EntityStatus, 'Status'),
    },
  ]);

  useEffect(() => {
    let filteredSites = sites.data || [];

    siteStatusFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0 && filter.id === 'status') {
        filteredSites = filteredSites.filter((site) =>
          activeFilterValues.includes(String(site.status)),
        );
      }
    });

    setFilteredSites(filteredSites);
  }, [siteStatusFilters, sites.data]);

  useEffect(() => {
    let filteredSubjects = subjects.data || [];

    subjectStatusFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0 && filter.id === 'status') {
        filteredSubjects = filteredSubjects.filter((subject) =>
          activeFilterValues.includes(String(subject.status)),
        );
      }
    });

    setFilteredSubjects(filteredSubjects);
  }, [subjectStatusFilters, subjects.data]);

  const createSite = useMutation(
    createSiteMutateOptions({
      onSuccess: (site) => {
        queryClient.setQueryData(
          sitesQueryOptions({ studyKey }).queryKey,
          (sites: Site[] | undefined) => [...(sites ?? []), site],
        );
      },
    }),
  );

  const createSubject = useMutation(
    createSubjectMutateOptions({
      onSuccess: (subject) => {
        queryClient.setQueryData(
          subjectsQueryOptions({ siteKey: subject.siteKey }).queryKey,
          (subjects: Subject[] | undefined) => [...(subjects ?? []), subject],
        );
      },
    }),
  );

  const getStatusLabel = (status: EntityStatus) => {
    return i18n.t(`Status.${EntityStatus[status].toLowerCase()}`);
  };

  const siteNameByKey = useMemo(
    () => new Map((sites.data || []).map((site) => [site.key, site.name])),
    [sites.data],
  );

  const sitesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.name'), dataKey: 'name' },
    { label: i18n.t('Entity.region'), dataKey: 'region' },
    {
      label: i18n.t('Entity.status'),
      dataKey: 'status',
      getSortValue: (site: Site) => getStatusLabel(site.status),
    },
  ];
  const sitesTableCellRenderer = (dataKey: string, site: Site) => {
    if (dataKey === 'name') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToSite(site.key, site.name)}
            type="$bodySmall.default"
          >
            {site[dataKey]}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'status') {
      return (
        <Table.TextCell key={dataKey}>
          {getStatusLabel(site.status)}
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{site[dataKey]}</Table.TextCell>;
    }
  };

  const subjectsTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Entity.name'), dataKey: 'name' },
    {
      label: i18n.t('Entity.site'),
      dataKey: 'siteKey',
      getSortValue: (subject: Subject) =>
        siteNameByKey.get(subject.siteKey) || '',
    },
    {
      label: i18n.t('Entity.status'),
      dataKey: 'status',
      getSortValue: (subject: Subject) => getStatusLabel(subject.status),
    },
  ];

  const devicesTableColumnData: ColumnConfig[] = [
    { label: i18n.t('Device Name'), dataKey: 'appName' },
    { label: i18n.t('Entity.id'), dataKey: 'deviceId' },
    { label: i18n.t('Device.os'), dataKey: 'os' },
    { label: i18n.t('Device.model'), dataKey: 'phoneModel' },
  ];

  const subjectsTableCellRenderer = (dataKey: string, subject: Subject) => {
    if (dataKey === 'siteKey') {
      const siteName = siteNameByKey.get(subject.siteKey) || '';

      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToSite(subject.siteKey, siteName)}
            type="$bodySmall.default"
          >
            {siteName}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'name') {
      return (
        <Table.TextCell key={dataKey}>
          <LinkText
            onPress={() => navigateToSubject(subject.key, subject.name)}
            type="$bodySmall.default"
          >
            {subject[dataKey]}
          </LinkText>
        </Table.TextCell>
      );
    } else if (dataKey === 'status') {
      return (
        <Table.TextCell key={dataKey}>
          {getStatusLabel(subject.status)}
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{subject[dataKey]}</Table.TextCell>;
    }
  };

  return (
    <VStack gap="$4" height="100%">
      {study.isPending ? (
        <VStack alignItems="center" justifyContent="center" flexGrow={1}>
          <LoadingSpinner testID="study-loading-spinner" />
        </VStack>
      ) : null}

      {study.isError ? (
        <MessageBanner
          type="danger"
          headerText={i18n.t('Page_Study.load_error_title')}
          bodyText={i18n.t('Page_Study.load_error')}
        />
      ) : null}

      {study.isSuccess ? (
        <VStack gap="$4">
          <StudyHeader study={study.data} />
          <DynamicTable
            isLoading={sites.isPending}
            hasLoadingError={sites.isError}
            title={i18n.t('Page_Study.table_sites_title')}
            description={i18n.t('Page_Study.table_sites_description')}
            columnData={sitesTableColumnData}
            rowData={filteredSites}
            cellRenderer={sitesTableCellRenderer}
            enableFiltering={true}
            checkboxFilters={siteStatusFilters}
            onFiltersChange={setSiteStatusFilters}
            onFiltersReset={() => {
              setSiteStatusFilters((prevFilters) =>
                prevFilters.map((filter) => ({
                  ...filter,
                  items: filter.items.map((item) => ({
                    ...item,
                    value: false,
                  })),
                })),
              );
            }}
          >
            <CreateSiteModal
              studyKey={study.data.key}
              trigger={
                <Button
                  size="medium"
                  label={i18n.t('Page_Study.add_site')}
                  onPress={() => {}}
                  disabled={createSite.isPending}
                />
              }
              onSuccess={async (newSite: Site) => {
                await createSite.mutateAsync({ site: newSite });
              }}
            />
          </DynamicTable>
          <DynamicTable
            isLoading={subjects.pending}
            hasLoadingError={false}
            title={i18n.t('Page_Study.table_subjects_title')}
            description={i18n.t('Page_Study.table_subjects_description')}
            columnData={subjectsTableColumnData}
            rowData={filteredSubjects}
            cellRenderer={subjectsTableCellRenderer}
            enableFiltering={true}
            checkboxFilters={subjectStatusFilters}
            onFiltersChange={setSubjectStatusFilters}
            onFiltersReset={() => {
              setSubjectStatusFilters((prevFilters) =>
                prevFilters.map((filter) => ({
                  ...filter,
                  items: filter.items.map((item) => ({
                    ...item,
                    value: false,
                  })),
                })),
              );
            }}
          >
            <CreateSubjectModal
              studyKey={study.data.key}
              sites={sites.data || []}
              trigger={
                <Button
                  size="medium"
                  label={i18n.t('Page_Study.add_subject')}
                  onPress={() => {}}
                  disabled={createSubject.isPending}
                />
              }
              onSuccess={async (newSubject: Subject) => {
                await createSubject.mutateAsync({ subject: newSubject });
              }}
            />
          </DynamicTable>
          <DynamicTable
            isLoading={devices.isPending}
            hasLoadingError={devices.isError}
            title={i18n.t('Page_Study.table_devices_title')}
            description={i18n.t('Page_Study.table_devices_description')}
            columnData={devicesTableColumnData}
            rowData={devices.data || []}
          >
            <AssignDeviceModal
              entityType={EntityType.Study}
              entityKey={study.data.key}
              entityName={study.data.name}
              trigger={
                <Button
                  size="medium"
                  label={i18n.t('Page_Study.manage_assignments')}
                  onPress={() => {}}
                  disabled={devices.isPending}
                />
              }
            />
          </DynamicTable>
        </VStack>
      ) : null}
    </VStack>
  );
}
